// App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate,Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoginForm from './components/LoginForm';
import Dashboard from './components/Dashboard';
import DashboardContent from './components/DashboardContent';
import ImportBatchPage from './pages/transaction/ImportBatchPage';
import DepartmentPage from './pages/DepartmentPage';
import EmployeePage from './pages/EmployeePage';
import UserPage from './pages/UserPage';
import StockPage from './pages/StockPage';
import PurityPage from './pages/PurityPage';
import ColorPage from './pages/ColorPage';
import StonePage from './pages/StonePage';
import RawStockCodePage from './pages/rawStockCode/RawStockCodePage';
import IssueRawStockPage from './pages/transaction/IssueRawStockPage';
import ReceiveRawStockPage from './pages/transaction/ReceiveRawStockPage';
import BrokenStoneEntryPage from './pages/transaction/BrokenStoneEntryPage';
import RawStockPage from './pages/transaction/RawStockPage';
import RawStockHistoryPage from './pages/transaction/RawStockHistoryPage';
import CsvImportLogsPage from './pages/csvImportLogs/CsvImportLogsPage';
import RawGoldIssuedReportPage from './pages/report/RawGoldIssuedReportPage';
import GoldStockAddedReportPage from './pages/report/GoldStockAddedReportPage';
import StoneIssuedReportPage from './pages/report/StoneIssuedReportPage';
import ImportBatchDetailPage from './pages/csvImportLogs/ImportBatchDetailPage';
import ProtectedRoute from './components/protectedRoutes';
import { checkAuthStatus } from './redux/actions/login/loginAction';
import DepartmentProcessPage from './pages/transaction/DepartmentProcessPage';
import InventoryProcessListPage from './pages/transaction/InventoryProcessListPage';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import theme from './theme';
import DepartmentWisePendingReportPage from './pages/report/DepartmentWisePendingReportPage';
import StockIssuePage from './pages/transaction/StockIssuePage';
import StockReceivePage from './pages/transaction/StockReceivePage';
import StockReturnPage from './pages/transaction/StockReturnPage';
import RawMaterialStockReportPage from './pages/report/RawMaterialStockReportPage';
import StoneStockAddedReportPage from './pages/report/StoneStockAddedReportPage';
import DepartmentWiseProductionReportPage from './pages/report/DepartmentWiseProductionReportPage';
import StockReceivedReportPage from './pages/report/StockReceivedReportPage';
import ActivityLogPage from './pages/ActivityLogPage';


function App() {
  const dispatch = useDispatch();
  const { authChecking, user } = useSelector((state) => state.login);
  useEffect(() => {
    // Check authentication status when app loads
    dispatch(checkAuthStatus());
  }, [dispatch]);

  // Optional: Show loading spinner while checking auth status
  if (authChecking) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          limit={3}
        />
        <Routes>
          <Route path="/login" element={<LoginForm />} />
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<DashboardContent />} />
            <Route path="import-batch" element={<ImportBatchPage />} />
            <Route path="csv-import-logs" element={<CsvImportLogsPage />} />
            <Route path="csv-import-batch/:referenceNumber" element={<ImportBatchDetailPage />} />
            <Route path="department-process" element={<DepartmentProcessPage />} />
            
            {/* Transaction Routes */}
            <Route path="transaction">
              <Route path="raw-stock" element={<RawStockPage />} />
              <Route path="raw-stock-history" element={<RawStockHistoryPage />} />
              <Route path="issue-raw-stock" element={<IssueRawStockPage />} />
              <Route path="receive-raw-stock" element={<ReceiveRawStockPage />} />
              <Route path="broken-stone-entry" element={<BrokenStoneEntryPage />} />
              <Route path="inventory-process" element={<DepartmentProcessPage />} />
              <Route path="inventory-process/:id" element={<DepartmentProcessPage />} />
              <Route path="inventory-process-list" element={<InventoryProcessListPage />} />
              <Route path="stock-issue" element={<StockIssuePage />} />
              <Route path="stock-receive" element={<StockReceivePage />} />
              <Route path="stock-return" element={<StockReturnPage />} />
            </Route>
            <Route path="activity-log" element={<ActivityLogPage />} />
            <Route path="/not-authorized" element={<div>You are not authorized to view this page.</div>} /> 
            {/* Masters Routes */}
            <Route path="masters" element={
              user && user?.role === 'Admin' ? <Outlet /> : <Navigate to="/not-authorized" replace />
            }>
              <Route path="user" element={<UserPage />} />
              <Route path="department" element={<DepartmentPage />} />
              <Route path="employee" element={<EmployeePage />} />
              <Route path="stock" element={<StockPage />} />
              <Route path="purity" element={<PurityPage />} />
              <Route path="color" element={<ColorPage />} />
              <Route path="stone" element={<StonePage />} />
              <Route path="raw-stock-code" element={<RawStockCodePage />} />
            </Route>

            <Route path="reports">
              <Route path="department-wise-pending" element={<DepartmentWisePendingReportPage />} />
              <Route path="department-wise-production" element={<DepartmentWiseProductionReportPage />} />
              <Route path="raw-material-stock" element={<RawMaterialStockReportPage />} />
              <Route path="raw-gold-issued" element={<RawGoldIssuedReportPage />} />
              <Route path="stone-issued" element={<StoneIssuedReportPage />} />
              <Route path="gold-stock-added" element={<GoldStockAddedReportPage />} />
              <Route path="stone-stock-added" element={<StoneStockAddedReportPage />} />
              <Route path="stock-received" element={<StockReceivedReportPage />} />
            </Route>

            <Route index element={<Navigate to="dashboard" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
